<script setup lang="ts">
import { computed } from 'vue';
import { useDownloadManagerStore } from '@/stores/downloadManagerStore';
import { TaskStatus, type VaultZipDownloadPayload } from '@/api';
import { useGaiaToast } from '@/composables/useGaiaToast';
import GaiaIconButton from '@/components/design/base/GaiaIconButton.vue';
import GaiaTooltip from '@/components/design/base/GaiaTooltip.vue';
import GaiaTaskPanel from '@/components/design/composite/GaiaTaskPanel.vue';
import GaiaTaskRow from '@/components/design/composite/GaiaTaskRow.vue';
import type { GaiaIndicatorStatus } from '@/components/design/base/GaiaStatusIndicator.vue';

const downloadManager = useDownloadManagerStore();
const message = useGaiaToast();

const headerLabel = computed(() => {
  const active = downloadManager.activeJobCount;
  if (active > 0) {
    return `Zipping ${active} folder${active > 1 ? 's' : ''}…`;
  }
  return 'Downloads';
});

/** The payload's status strings, translated to the design system's. */
function rowStatus(job: VaultZipDownloadPayload): GaiaIndicatorStatus {
  if (job.status === TaskStatus.RUNNING) return 'running';
  if (job.status === TaskStatus.DONE) return 'success';
  return 'alert';
}

function rowDetail(job: VaultZipDownloadPayload): string {
  if (job.status === TaskStatus.RUNNING)
    return `${job.processed_files} of ${job.total_files} files`;
  if (job.status === TaskStatus.DONE) return 'Completed';
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  return job.error || 'The download failed.';
}

function progressPercentage(job: VaultZipDownloadPayload): number | undefined {
  if (job.status !== TaskStatus.RUNNING) return undefined;
  if (job.total_files <= 0) return 0;
  return Math.min(100, Math.round((job.processed_files / job.total_files) * 100));
}

async function handleRedownload(job: VaultZipDownloadPayload): Promise<void> {
  await downloadManager.redownloadJob(job.uid);
}

async function handleRetry(job: VaultZipDownloadPayload): Promise<void> {
  try {
    await downloadManager.retryJob(job.uid);
  } catch (error) {
    message.error(
      `Failed to restart folder download: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}
</script>

<template>
  <gaia-task-panel
    v-if="downloadManager.hasJobs"
    :title="headerLabel"
    :count="downloadManager.activeJobCount"
  >
    <gaia-task-row
      v-for="job in downloadManager.jobs"
      :key="job.uid"
      :status="rowStatus(job)"
      :label="job.file_name"
      :detail="rowDetail(job)"
      :percentage="progressPercentage(job)"
    >
      <template #actions>
        <gaia-tooltip v-if="job.artifact_uid && job.status !== TaskStatus.RUNNING">
          <template #trigger>
            <gaia-icon-button :dimension="24" quaternary @click="handleRedownload(job)">
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M8 2.5v8M4.5 7 8 10.5 11.5 7M3 13h10" />
              </svg>
            </gaia-icon-button>
          </template>
          Download again
        </gaia-tooltip>

        <gaia-tooltip v-if="job.status === TaskStatus.FAILED && !job.artifact_uid">
          <template #trigger>
            <gaia-icon-button :dimension="24" quaternary @click="handleRetry(job)">
              <svg
                width="13"
                height="13"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M13.5 8a5.5 5.5 0 1 1-1.6-3.9M13.5 2.5v2.6h-2.6" />
              </svg>
            </gaia-icon-button>
          </template>
          Retry
        </gaia-tooltip>

        <gaia-tooltip>
          <template #trigger>
            <gaia-icon-button
              :dimension="24"
              quaternary
              @click="downloadManager.dismissJob(job.uid)"
            >
              <svg
                width="11"
                height="11"
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
              >
                <path d="M4 4l8 8M12 4l-8 8" />
              </svg>
            </gaia-icon-button>
          </template>
          Dismiss
        </gaia-tooltip>
      </template>
    </gaia-task-row>
  </gaia-task-panel>
</template>
